import { GridPinnedColumns } from '@chronosphereio/chrono-ui';
import { MouseEventHandler, ReactElement } from 'react';
import { LegendOptionsBase, UnitOptions } from '../../core';

// This file contains legend-related model code specific to the legend component.
// See the `core` package for common/shared legend model code and the
// `plugin-system` package for legend model code specific to panel plugin specs.

// Note: explicitly defining different options for the legend component and
// legend spec that extend from some common options, so we can allow the
// component and the spec to diverge in some upcoming work.
export type LegendComponentOptions = LegendOptionsBase;

export interface LegendItem {
  id: string;
  label: string;
  color: string;
  icon?: ReactElement;
  unit?: UnitOptions;
  isComparison?: boolean;

  /**
   * Additional data for the legend item. Useful for laying out additional
   * columns when using a table legend.
   */
  data?: Record<string, unknown>;

  onClick?: MouseEventHandler<HTMLElement>;
}

export const LEGEND_DENSITIES = ['compact', 'standard'] as const;
export type LegendDensity = (typeof LEGEND_DENSITIES)[number];
export const DEFAULT_LEGEND_DENSITY: LegendDensity = 'compact';

export type SelectedLegendItemRecordState = Record<LegendItem['id'], boolean>;

// TODO: Simplify this by removing "ALL".
// Will be easier when ContentWithLegend is not used outside of viz-framework.
/**
 * State of selected items in the legend.
 *
 * - When "ALL", all legend items are selected.
 * - Otherwise, it is a Record that associates legend item IDs with a boolean
 *   value. When the associated entry for a legend item is `true`, that item
 *   will be treated as selected.
 */
export type SelectedLegendItemState = SelectedLegendItemRecordState | 'ALL';

/**
 * Configuration for pinning table legend columns to the left and/or right.
 * Each property is a pinning direction (`left` or `right`) set to an an array of column `field`s to
 * identify which columns to pin in that direction.
 */
export type TableLegendPinnedColumns = GridPinnedColumns;

/**
 * Returns a full key-value store of selected legend items initialized with the specified
 * selected state.
 */
export function getAllLegendItemsSelectedState(
  items: LegendItem[],
  defaultSelectedState = true
): SelectedLegendItemRecordState {
  const updatedRowSelection: SelectedLegendItemState = {};
  for (const item of items) {
    updatedRowSelection[item.id] = defaultSelectedState;
  }
  return updatedRowSelection;
}

/**
 * Returns a key-value store of selected legend items with a single item selected and all other
 * items unselected.
 */
export function getSingleLegendItemSelectedState(
  items: LegendItem[],
  selectedItemId: string
): SelectedLegendItemRecordState {
  const updatedRowSelection: SelectedLegendItemState = getAllLegendItemsSelectedState(items, false);
  updatedRowSelection[selectedItemId] = true;

  return updatedRowSelection;
}

export function isOnlyLegendItemSelected(item: Pick<LegendItem, 'id'>, selectedItems: SelectedLegendItemState) {
  if (selectedItems === 'ALL') return false;

  const selectedIds = Object.entries(selectedItems)
    .filter(([_, isSelected]) => !!isSelected)
    .map(([id, _]) => id);

  return selectedIds.length === 1 && selectedIds[0] === item.id;
}

export function isLegendItemSelected(item: Pick<LegendItem, 'id'>, selectedItems: SelectedLegendItemState) {
  return selectedItems === 'ALL' || !!selectedItems[item.id];
}

/**
 * Check whether color manipulation is valid since color passed to legend might not be.
 */
export function safeColorManipulate(color: string, fallbackColor: string, manipulateFn: () => string) {
  try {
    // Attempt to apply the color manipulation function
    return manipulateFn();
  } catch (_error) {
    // If an error occurs (invalid color), return the fallback color
    return fallbackColor;
  }
}

// When the number of items to display is above this number, it is likely to
// cause performance issues in the browser. The legend will be displayed in a
// format (list) that allows for virtualization to minimize the performance impact.
// Set this number based on testing, but it may need to be tuned a bit on the
// future as people test this out on different machines.
const VIRTUALIZATION_LIMIT = 500;

type shouldRenderCompactListLegendProps = Pick<LegendComponentOptions, 'mode' | 'position'> & {
  totalLegendItems: number;
  disableCompactLegend?: boolean;
};

/**
 * Takes in legend information and returns true if it should be rendered as a
 * compact legend.
 */
export function shouldRenderCompactListLegend({
  mode,
  position,
  totalLegendItems,
  disableCompactLegend,
}: shouldRenderCompactListLegendProps) {
  if (disableCompactLegend) {
    return false;
  }

  const legendNeedsVirtualization = totalLegendItems >= VIRTUALIZATION_LIMIT;

  // The bottom legend is displayed as a list when the number of items is too
  // large and requires virtualization. Otherwise, it is rendered more compactly.
  // We do not need this check for the right-side legend because it is always
  // a virtualized list.
  return mode === 'List' && position === 'Bottom' && !legendNeedsVirtualization;
}
